import {
  birrInputValue,
  litersInputValue,
  ProjectionAssumptions,
  tryParseBirr,
  tryParseLiters,
  type ProjectionAssumptionsData,
  type ProjectionAssumptionKey,
} from '@dairy/shared';

export const assumptionGroups: Array<{ name: string; keys: ProjectionAssumptionKey[] }> = [
  {
    name: 'milkFeed',
    keys: [
      'milkPriceSantim',
      'feedPerCowDaySantim',
      'feedPerLiterSantim',
      'calfMilkMlPerDay',
      'calfMilkDays',
      'calfFeedPerDaySantim',
      'youngFeedPerDaySantim',
      'heiferFeedPerDaySantim',
    ],
  },
  {
    name: 'healthBreeding',
    keys: ['vetPerAnimalYearSantim', 'aiCostPerServiceSantim', 'servicesPerConceptionX10', 'maturePeakMl'],
  },
  { name: 'calvesLabor', keys: ['calfValueFemaleSantim', 'calfValueMaleSantim', 'laborPerMonthSantim'] },
];
export const assumptionKeys = assumptionGroups.flatMap((group) => group.keys);
export const emptyAssumptions = () =>
  ProjectionAssumptions.parse(Object.fromEntries(assumptionKeys.map((key) => [key, null])));
export type AssumptionInputs = Record<ProjectionAssumptionKey, string>;
export type ScenarioInputs = Record<
  'low' | 'high',
  Record<'yieldBp' | 'priceBp' | 'feedBp' | 'spcAddX10', string>
>;
export const signedTenths = (value: number) =>
  `${value < 0 ? '-' : ''}${Math.floor(Math.abs(value) / 10)}.${Math.abs(value) % 10}`;
export const inputValue = (key: ProjectionAssumptionKey, value: number | null) =>
  value === null
    ? ''
    : key.endsWith('Santim')
      ? birrInputValue(value)
      : key.endsWith('Ml') || key === 'calfMilkMlPerDay'
        ? litersInputValue(value)
        : key === 'servicesPerConceptionX10'
          ? signedTenths(value)
          : String(value);
export const inputsFrom = (data: ProjectionAssumptionsData): AssumptionInputs =>
  Object.fromEntries(assumptionKeys.map((key) => [key, inputValue(key, data[key])])) as AssumptionInputs;
export const scenarioInputsFrom = (data: ProjectionAssumptionsData): ScenarioInputs =>
  Object.fromEntries(
    (['low', 'high'] as const).map((kind) => [
      kind,
      Object.fromEntries(
        Object.entries(data.scenarios[kind]).map(([key, value]) => [
          key,
          key === 'spcAddX10' ? signedTenths(value) : birrInputValue(value),
        ]),
      ),
    ]),
  ) as ScenarioInputs;

function tenths(raw: string): number | undefined {
  const match = /^([+-]?)(\d+)(?:[.,](\d))?$/.exec(raw.trim());
  return match ? (match[1] === '-' ? -1 : 1) * (Number(match[2]) * 10 + Number(match[3] ?? 0)) : undefined;
}
export function parseAssumptionInputs(inputs: AssumptionInputs, advanced: ScenarioInputs) {
  const data = structuredClone(emptyAssumptions());
  const errors: Record<string, boolean> = {};
  const shapes = ProjectionAssumptions.omit({ scenarios: true }).shape;
  for (const key of assumptionKeys) {
    const raw = inputs[key].trim();
    if (!raw) {
      data[key] = null;
      continue;
    }
    let value: number | undefined;
    if (key.endsWith('Santim')) {
      const parsed = tryParseBirr(raw);
      value = parsed.ok ? (parsed.santim ?? undefined) : undefined;
    } else if (key.endsWith('Ml') || key === 'calfMilkMlPerDay') {
      const parsed = tryParseLiters(raw);
      value = parsed.ok ? (parsed.ml ?? undefined) : undefined;
    } else if (key === 'servicesPerConceptionX10') value = tenths(raw);
    else value = /^\d+$/.test(raw) ? Number(raw) : undefined;
    if (value === undefined || !shapes[key].safeParse(value).success) errors[key] = true;
    else data[key] = value;
  }
  for (const kind of ['low', 'high'] as const)
    for (const key of ['yieldBp', 'priceBp', 'feedBp', 'spcAddX10'] as const) {
      const raw = advanced[kind][key].trim();
      const percent = tryParseBirr(raw);
      const value =
        key === 'spcAddX10' ? tenths(raw) : percent.ok ? (percent.santim ?? undefined) : undefined;
      const proposed = { ...data.scenarios[kind], [key]: value };
      if (
        value === undefined ||
        !ProjectionAssumptions.shape.scenarios.unwrap().shape[kind].safeParse(proposed).success
      )
        errors[`${kind}.${key}`] = true;
      else data.scenarios[kind][key] = value;
    }
  return { data, errors };
}
